import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { promotePoa } from "@/features/eservices/api";
import type { PoaPendingRow } from "@/features/eservices/types";

interface PromotePoaDialogProps {
  row: PoaPendingRow | null;
  onOpenChange: (open: boolean) => void;
  onPromoted: () => void;
}

/** Promote a submitted POA doc to nightly CAF monitoring (legacy poa_to_monitor). */
export function PromotePoaDialog({ row, onOpenChange, onPromoted }: PromotePoaDialogProps) {
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [autoCheck, setAutoCheck] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setYearFrom("");
    setYearTo("");
    setAutoCheck(true);
  }, [row]);

  const submit = async () => {
    if (!row) return;
    setSubmitting(true);
    try {
      await promotePoa(row.id, {
        form: row.form,
        yearFrom: yearFrom ? Number(yearFrom) : undefined,
        yearTo: yearTo ? Number(yearTo) : undefined,
        autoCheck,
      });
      onPromoted();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start monitoring.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={row !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Monitor CAF approval</DialogTitle>
        </DialogHeader>
        {row && (
          <div className="flex flex-col gap-4">
            <p className="text-muted-foreground text-sm">
              Form {row.form} for {row.clientName}. The nightly check watches for
              CAF approval and drops it after 12 weeks pending.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="yf">Year from</Label>
                <Input
                  id="yf"
                  inputMode="numeric"
                  value={yearFrom}
                  onChange={(e) => setYearFrom(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="yt">Year to</Label>
                <Input
                  id="yt"
                  inputMode="numeric"
                  value={yearTo}
                  onChange={(e) => setYearTo(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="auto"
                checked={autoCheck}
                onCheckedChange={(v) => setAutoCheck(Boolean(v))}
              />
              <Label htmlFor="auto" className="text-sm">
                Check automatically each night
              </Label>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? "Starting…" : "Start monitoring"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
